import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * MoreSheet — mobile bottom sheet listing the nav items that don't fit
 * in the 5-slot bottom navigation. UI-only: navigation uses the same
 * <Link> mechanism as the sidebar.
 *
 * Deliberately NOT wrapped in AnimatePresence: repeated open/close left the
 * overlay stuck because AnimatePresence waited on a nested exit animation that
 * didn't reliably complete. Conditional render unmounts instantly on close, so
 * every dismissal path is reliable; the slide-up entrance is kept via motion.
 */
const MoreSheet = ({ open, onClose, items, isActive }) => {
  const prefersReducedMotion = useReducedMotion();
  if (!open) return null;

  const panelMotion = prefersReducedMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.15 } }
    : { initial: { y: '100%' }, animate: { y: 0 }, transition: { type: 'tween', duration: 0.25, ease: [0.32, 0.72, 0, 1] } };

  return (
    <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label="More navigation">
      {/* Backdrop — tap to close */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet panel */}
      <motion.div
        {...panelMotion}
        className="absolute bottom-0 inset-x-0 rounded-t-2xl bg-white dark:bg-dark-900 border-t border-gray-100 dark:border-dark-800 shadow-[0_-8px_30px_rgba(0,0,0,0.12)] dark:shadow-[0_-8px_30px_rgba(0,0,0,0.5)]"
      >
        {/* Grab handle */}
        <div className="flex justify-center pt-3 pb-1">
          <span className="w-10 h-1 rounded-full bg-gray-200 dark:bg-dark-700" />
        </div>

        <nav className="px-3 pt-1 pb-safe">
          <div className="space-y-0.5 pb-2">
            {items.map((item) => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={onClose}
                  className={`relative flex items-center gap-3.5 min-h-[48px] px-3 py-2.5 rounded-xl transition-colors duration-150 ${
                    active
                      ? 'bg-gradient-to-r from-pink-500/10 to-violet-600/10 text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-400 active:bg-gray-50 dark:active:bg-dark-800/60'
                  }`}
                >
                  {active && (
                    <span className="absolute left-0 top-2 bottom-2 w-1 rounded-full bg-gradient-to-b from-pink-500 to-violet-600" />
                  )}

                  <span className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${
                    active
                      ? 'bg-gradient-to-br from-pink-500 to-violet-600 text-white shadow-sm'
                      : 'bg-gray-100 dark:bg-dark-800 text-gray-500 dark:text-gray-400'
                  }`}>
                    <item.icon className="w-5 h-5" />
                  </span>

                  <span className={`text-sm ${active ? 'font-semibold' : 'font-medium'}`}>
                    {item.name}
                  </span>

                  {item.badge && (
                    <span className="ml-auto text-[10px] font-semibold bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 px-1.5 py-0.5 rounded-full flex-shrink-0">
                      {item.badge}
                    </span>
                  )}

                  {active && !item.badge && (
                    <span className="ml-auto w-1.5 h-1.5 rounded-full bg-gradient-to-r from-pink-500 to-violet-600 flex-shrink-0" />
                  )}
                </Link>
              );
            })}
          </div>
        </nav>
      </motion.div>
    </div>
  );
};

export default MoreSheet;
